import { useQuery } from "@tanstack/react-query";

import { listRdos, type RdoStatus } from "@/adapters/api";
import type { RdoSummary } from "../types.ts";
import { toRdoSummary } from "../utils/map.ts";

export const RDOS_PAGE_SIZE = 25;

export interface RdosFilters {
	from?: string;
	to?: string;
	status?: RdoStatus;
	includeSuperseded?: boolean;
	page?: number;
}

export interface RdosPage {
	count: number;
	items: RdoSummary[];
}

export function rdosQueryKey(constructionId: string | undefined) {
	return ["rdos", constructionId] as const;
}

export function useRdos(
	constructionId: string | undefined,
	filters: RdosFilters,
) {
	return useQuery({
		queryKey: [
			...rdosQueryKey(constructionId),
			filters.from ?? "",
			filters.to ?? "",
			filters.status ?? "",
			filters.includeSuperseded ? "1" : "0",
			filters.page ?? 1,
		],
		queryFn: async (): Promise<RdosPage> => {
			// biome-ignore lint/style/noNonNullAssertion: guarded by `enabled`
			const response = await listRdos(constructionId!, {
				from: filters.from,
				to: filters.to,
				status: filters.status,
				includeSuperseded: filters.includeSuperseded,
				page: filters.page ?? 1,
				pageSize: RDOS_PAGE_SIZE,
			});
			return {
				count: response.count,
				items: response.items.map(toRdoSummary),
			};
		},
		enabled: !!constructionId,
		staleTime: 15_000,
		placeholderData: (previous) => previous,
	});
}
